import { useEffect, useState } from 'react';

import { brandTokens } from '~/services/branding/brandingColors';
import { ClientBranding, subscribeClientBranding } from '~/services/branding/clientBranding';

/**
 * Dernière marque connue par établissement, gardée le temps de la page : la TV passe
 * sans cesse du menu (HomeContent) au jeu (ScreenApp), chacun monte son propre suivi.
 * Sans ce souvenir, chaque passage repartirait de `null` → logo et couleurs Unboared
 * affichés un instant avant ceux du bar.
 */
const lastKnown = new Map<string, ClientBranding | null>();

/**
 * Marque de l'établissement à AFFICHER (TV, téléphone) : `null` tant qu'elle
 * n'est pas lue, absente, ou pas activée (`enabled` n'est vrai qu'en formule
 * Pro, cf. services/branding/clientBranding.ts). Lecture en direct : un
 * changement fait dans Mon compte arrive sur la TV sans recharger.
 * Les démos n'ont pas de marque.
 */
export function useClientBranding(clientID: string | null | undefined): ClientBranding | null {
    const [branding, setBranding] = useState<ClientBranding | null>(() =>
        clientID ? (lastKnown.get(clientID) ?? null) : null
    );

    useEffect(() => {
        setBranding(clientID ? (lastKnown.get(clientID) ?? null) : null);
        if (!clientID || clientID === 'demo') return undefined;
        return subscribeClientBranding(
            clientID,
            (b) => {
                const shown = b.enabled ? b : null;
                lastKnown.set(clientID, shown);
                setBranding(shown);
            },
            // Illisible (réseau, règles) : écran Unboared normal, sans bruit pour le joueur.
            () => setBranding(null)
        );
    }, [clientID]);

    return branding;
}

/**
 * Pose les couleurs de la marque sur `:root` le temps que l'écran est monté
 * (après le CSS de l'app : gagne la cascade, comme la charte Orly). Le logo
 * passe aussi par `--logo-url` pour les cadres des jeux (components/design/logo).
 */
export function useBrandTheme(branding: ClientBranding | null) {
    useEffect(() => {
        if (!branding) return undefined;
        const root = document.documentElement;
        const tokens: Record<string, string> = brandTokens(branding.primary, branding.secondary);
        if (branding.logo) tokens['--logo-url'] = `url("${branding.logo}")`;
        Object.entries(tokens).forEach(([k, v]) => root.style.setProperty(k, v));
        return () => Object.keys(tokens).forEach((k) => root.style.removeProperty(k));
    }, [branding]);
}
